import { resolveStoredFileUrl } from "utils/storedFileUrl";
// React
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// @mui material components
import { Card, Fade, Grid, Icon, ImageList, ImageListItem, Modal, Tooltip } from "@mui/material";

import { gql, useMutation, useQuery } from "@apollo/client";
import SinDatos from "components/Carteles/SinDatos";
import ModalConfirm from "components/Modals/Confirm";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { getUproColor } from "utils/colors";
import { formatMoneyPunto } from "utils/formatters";
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "40%", xxl: "30%" },
  overflowY: "auto",
  p: 3,
};

const CAMBIAR_ESTADO = gql`
  mutation cambiarEstadoPago($id: ID!, $estado: String!) {
    cambiarEstadoPago(id: $id, estado: $estado) {
      id
    }
  }
`;
const GET_PEDIDO_SPLIT = gql`
  query getPedidoSplitResumen($id: ID!) {
    pedido(id: $id) {
      id
      total
      pagoSplitCC
      cuentaCorriente
    }
  }
`;

export default function ModalCompobantes({ open, handleClose, data, idPedido, refetch }) {
  const [files, setFiles] = useState([]);
  const [openConfirm, setOpenConfirm] = useState(false);
  const { handleSnackbar } = useContext(MessageManager);
  useEffect(() => {
    if (data) {
      setFiles(data.comprobanteFile || []);
    }
  }, [data]);
  const handleClickDocument = (url) => {
    window.open(
      resolveStoredFileUrl(url),
      "_blank",
      "toolbar=yes,scrollbars=yes,resizable=yes,top=500,left=500,width=400,height=400"
    );
  };

  const [cambiarEstadoPago, { loading }] = useMutation(CAMBIAR_ESTADO);
  const { data: pedidoSplitData } = useQuery(GET_PEDIDO_SPLIT, {
    variables: { id: idPedido },
    skip: !open || !idPedido,
    fetchPolicy: "no-cache",
  });

  const pedidoSplit = pedidoSplitData?.pedido || null;
  const montoCuentaCorrienteSplit = Number(pedidoSplit?.pagoSplitCC || 0);
  const esPagoSplit = montoCuentaCorrienteSplit > 0;
  const totalPedidoSplit = Number(pedidoSplit?.total || 0);
  const montoTransferenciaSplit = Number(data?.totalPago || 0);

  const handleCambiarEstado = async (estado) => {
    try {
      if (!data?.id) {
        await refetch?.();
        handleSnackbar(
          "No se pudo identificar el pago. La lista fue actualizada; vuelva a abrir el comprobante",
          "warning"
        );
        handleClose();
        return;
      }

      await cambiarEstadoPago({ variables: { id: data.id, estado } });
      if (estado === "Aprobado") {
        handleSnackbar("Comprobante aprobado correctamente", "success");
      } else {
        handleSnackbar("Comprobante rechazado, el pedido fue anulado", "success");
      }
      await refetch?.();
      handleClose();
    } catch (error) {
      handleSnackbar(error?.message || "No se pudo actualizar el comprobante", "error");
    }
  };

  const [mensaje, setMensaje] = useState("");
  const [estado, setEstado] = useState("");
  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between">
              <SoftBox>
                <SoftTypography variant="h6">
                  Comprobantes de pago - Pedido N° {idPedido} -{" "}
                  <SoftBox
                    component="span"
                    color={
                      data?.estado === "Aprobado"
                        ? getUproColor("uproGreen")
                        : data?.estado === "Rechazado"
                          ? getUproColor("error")
                          : getUproColor("uproYellow")
                    }
                    fontWeight="bold"
                  >
                    {data?.estado}
                  </SoftBox>
                </SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="space-between">
                <SoftBox>
                  <Tooltip title="Cerrar" placement="top">
                    <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <Grid container spacing={2}>
              {esPagoSplit && (
                <Grid item xs={12}>
                  <SoftBox
                    mt={1}
                    p={2}
                    sx={{
                      borderRadius: "0.75rem",
                      backgroundColor: "rgba(59, 130, 246, 0.08)",
                      border: "1px solid rgba(59, 130, 246, 0.24)",
                    }}
                  >
                    <SoftTypography variant="button" fontWeight="bold" color="info">
                      Pago con cuenta corriente + transferencia
                    </SoftTypography>
                    <SoftBox mt={1}>
                      <SoftTypography variant="caption" display="block">
                        Total del pedido: ${formatMoneyPunto(totalPedidoSplit)}
                      </SoftTypography>
                      <SoftTypography variant="caption" display="block">
                        Cubierto en cuenta corriente: $
                        {formatMoneyPunto(montoCuentaCorrienteSplit)}
                      </SoftTypography>
                      <SoftTypography variant="caption" display="block" fontWeight="bold">
                        Monto del comprobante a revisar: $
                        {formatMoneyPunto(montoTransferenciaSplit)}
                      </SoftTypography>
                    </SoftBox>
                  </SoftBox>
                </Grid>
              )}
              <Grid item xs={12}>
                {files.length > 0 ? (
                  <ImageList sx={{ height: 200 }} cols={3} rowHeight={164}>
                    {files.map((item, index) => (
                      <ImageListItem
                        key={index}
                        onClick={() => {
                          handleClickDocument(item);
                        }}
                      >
                        {item?.endsWith(".jpg") ||
                          item?.endsWith(".png") ||
                          item?.endsWith(".jpeg") ? (
                          <img
                            src={resolveStoredFileUrl(item)}
                            alt={`foto-${index}`}
                            loading="lazy"
                            style={{ objectFit: "contain", width: "100%", height: "100%" }}
                          />
                        ) : (
                          <SoftBox
                            width="100%"
                            height="100%"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            flexDirection="column"
                            gap={1}
                          >
                            <SoftBox
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              width="100px"
                              height="100px"
                              border="1px solid #ccc"
                              borderRadius="1rem"
                            >
                              <Icon fontSize="large">insert_drive_file</Icon>
                            </SoftBox>
                            <SoftTypography variant="caption">{item?.name}</SoftTypography>
                          </SoftBox>
                        )}
                      </ImageListItem>
                    ))}
                  </ImageList>
                ) : (
                  <SinDatos />
                )}
              </Grid>
            </Grid>
            <SoftBox
              display="flex"
              justifyContent="end"
              mt={2}
              gap={1}
              flexDirection={{ xs: "column", sm: "row" }}
            >
              <SoftButton
                color="error"
                circular
                onClick={() => {
                  setMensaje(
                    "¿Está seguro que desea rechazar el comprobante?, esto anulará el pedido"
                  );
                  setEstado("Rechazado");
                  setOpenConfirm(true);
                }}
                disabled={loading || data?.estado === "Aprobado" || data?.estado === "Rechazado"}
              >
                <Icon sx={{ fontWeight: "light" }}>cancel</Icon>
                &nbsp;Rechazar
              </SoftButton>
              <SoftButton
                color="success"
                circular
                onClick={() => {
                  setMensaje("¿Está seguro que desea aprobar el comprobante?");
                  setEstado("Aprobado");
                  setOpenConfirm(true);
                }}
                disabled={loading || data?.estado === "Aprobado" || data?.estado === "Rechazado"}
              >
                <Icon sx={{ fontWeight: "light" }}>check</Icon>
                &nbsp;Aprobar
              </SoftButton>
            </SoftBox>
          </SoftBox>
          <ModalConfirm
            open={openConfirm}
            handleClose={() => setOpenConfirm(false)}
            message={mensaje}
            func={() => {
              handleCambiarEstado(estado);
            }}
          />
        </Card>
      </Fade>
    </Modal>
  );
}

ModalCompobantes.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  data: PropTypes.object,
  idPedido: PropTypes.string,
  refetch: PropTypes.func,
};
